import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { useFetchData } from '../../../hooks/useFetchData/useFetchData';
import { useCryptoSessionStorage } from '../../../hooks/useCryptoSessionStorage';
import { useShowMessages } from '../../../hooks/useMessages/useMessages';
import AttendantQueue from './AttendantQueue';
import TimeCard from './TimeCard';
import SlaCard from './SlaCard';
import TotalsCard from './TotalsCard';

const urlOpenTickets = process.env.REACT_APP_API + '/blip/open-tickets';
const urlAttendants = process.env.REACT_APP_API + '/blip/attendants';
const urlTimings = process.env.REACT_APP_API + '/blip/timings';
const urlSla = process.env.REACT_APP_API + '/blip/sla';
const COLUMNS = 6;

const getQueueLayout = (total, isLargeScreen) => {
  if (!isLargeScreen) {
    return total <= COLUMNS ? { mode: 'single' } : { mode: 'wrap' };
  }
  if (total <= COLUMNS) {
    return { mode: 'single' };
  }
  if (total <= COLUMNS * 2) {
    return { mode: 'split' };
  }
  return { mode: 'multi' };
};

const getGridPlacement = (index, total) => {
  const layout = getQueueLayout(total);

  if (layout.mode === 'multi') {
    return {};
  }

  const col = (index % COLUMNS) + 1;
  const row = Math.floor(index / COLUMNS) + 1;

  if (layout.mode === 'single') {
    return {
      gridColumn: `${col} / span 1`,
      gridRow: '1 / span 1',
    };
  }

  if (row === 1) {
    const hasBelow = index + COLUMNS < total;
    return {
      gridColumn: `${col} / span 1`,
      gridRow: hasBelow ? '1 / span 1' : '1 / span 2',
    };
  }

  return {
    gridColumn: `${col} / span 1`,
    gridRow: '2 / span 1',
  };
};

const formatDuration = (value) => {
  if (!value || typeof value !== 'string') return '--';
  const parts = value.split(':');
  if (parts.length !== 3) return value;
  const [hoursRaw, minutesRaw, secondsRaw] = parts;
  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = hoursRaw.includes('.') ? hoursRaw : hoursRaw.padStart(2, '0');
  const minutes = minutesRaw.padStart(2, '0');
  const seconds = secondsPart.padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

const formatSecondsToDuration = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  const totalSeconds = Number(value);
  if (Number.isNaN(totalSeconds)) return '--';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
};

const parseDurationToSeconds = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split(':');
  if (parts.length !== 3) return null;
  const [hoursRaw, minutesRaw, secondsRaw] = parts;
  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsPart);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return null;
  }
  return hours * 3600 + minutes * 60 + seconds;
};

// [MUDANÇA] Parse robusto para tempos com possível formato "dias.horas:mm:ss"
const parseDurationToSecondsForRisk = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split(':');
  if (parts.length !== 3) return null;

  let [hoursRaw, minutesRaw, secondsRaw] = parts;
  let days = 0;
  if (hoursRaw.includes('.')) {
    const [daysStr, hoursStr] = hoursRaw.split('.');
    days = Number(daysStr);
    hoursRaw = hoursStr;
  }

  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsPart);

  if (
    !Number.isFinite(days) ||
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    !Number.isFinite(seconds)
  ) {
    return null;
  }

  return (days * 24 + hours) * 3600 + minutes * 60 + seconds;
};

export default function BlipMonitoringPage() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [changed, setChanged] = useState(false);
  const [secondsToRefresh, setSecondsToRefresh] = useState(30);
  const [slaDialogOpen, setSlaDialogOpen] = useState(false);
  // [MUDANÇA] Filtro rápido de tickets no monitoramento.
  const [ticketFilter, setTicketFilter] = useState('all');
  const [slaForm, setSlaForm] = useState({
    first_response_time: '',
    attendance_time: '',
    wait_time: '',
    response_time: '',
  });
  const { getData } = useCryptoSessionStorage();
  const { ShowMessage } = useShowMessages();
  const user = getData('user') || {};
  const { data } = useFetchData(urlOpenTickets, {}, changed, true, false, true);
  const { data: attendantsData } = useFetchData(urlAttendants, {}, changed, true, false, true);
  const { data: timingsData } = useFetchData(urlTimings, {}, changed, true, false, true);
  const { data: slaData, postData: postSla } = useFetchData(urlSla, {}, changed, true, false, true);

  const queues = useMemo(() => {
    const items = data?.items || [];
    const queueMap = new Map();

    items.forEach((ticket) => {
      const attendantId = ticket?.userData?.id || 'unknown';
      const attendantName = ticket?.userData?.name?.trim() || 'Sem atendente';
      const key = String(attendantId);

      if (!queueMap.has(key)) {
        queueMap.set(key, {
          id: attendantId,
          name: attendantName,
          tickets: [],
        });
      }

      queueMap.get(key).tickets.push(ticket);
    });

    return Array.from(queueMap.values());
  }, [data]);

  const attendantsById = useMemo(() => {
    const items = attendantsData?.items || [];
    const map = new Map();
    items.forEach((attendant) => {
      const id = attendant?.userData?.id;
      if (id !== undefined && id !== null) {
        map.set(String(id), attendant);
      }
    });
    return map;
  }, [attendantsData]);

  const totalsFromTimings = useMemo(() => {
    const opened = Number(timingsData?.open);
    const closed = Number(timingsData?.closed);
    return {
      opened: Number.isFinite(opened) ? opened : 0,
      closed: Number.isFinite(closed) ? closed : 0,
    };
  }, [timingsData]);

  // [MUDANÇA] Aplica filtros solicitados:
  // - SLA estourado (>100%)
  // - Acima de 30 min
  // - Aguardando resposta (sem 1ª resposta registrada)
  const filteredQueues = useMemo(() => {
    if (ticketFilter === 'all') return queues;

    const attendanceSlaSeconds = Number(slaData?.attendance_time);
    const firstResponseSlaSeconds = Number(slaData?.first_response_time);

    const matchTicket = (ticket) => {
      const attendanceSeconds = parseDurationToSecondsForRisk(ticket?.attendanceTime);
      const firstResponseSeconds = parseDurationToSecondsForRisk(ticket?.firstResponseTime);

      if (ticketFilter === 'above_30m') {
        return (
          (Number.isFinite(attendanceSeconds) && attendanceSeconds >= 1800) ||
          (Number.isFinite(firstResponseSeconds) && firstResponseSeconds >= 1800)
        );
      }

      if (ticketFilter === 'waiting_response') {
        return !Number.isFinite(firstResponseSeconds) || firstResponseSeconds <= 0;
      }

      if (ticketFilter === 'sla_overrun') {
        const percents = [];
        if (
          Number.isFinite(attendanceSeconds) &&
          Number.isFinite(attendanceSlaSeconds) &&
          attendanceSlaSeconds > 0
        ) {
          percents.push((attendanceSeconds / attendanceSlaSeconds) * 100);
        }
        if (
          Number.isFinite(firstResponseSeconds) &&
          Number.isFinite(firstResponseSlaSeconds) &&
          firstResponseSlaSeconds > 0
        ) {
          percents.push((firstResponseSeconds / firstResponseSlaSeconds) * 100);
        }
        return percents.some((percent) => percent > 100);
      }

      return true;
    };

    return queues
      .map((queue) => ({
        ...queue,
        tickets: (queue.tickets || []).filter(matchTicket),
      }))
      .filter((queue) => queue.tickets.length > 0);
  }, [queues, ticketFilter, slaData]);

  const queueCount = filteredQueues.length;
  const isLargeScreen = useMediaQuery('(min-width:1600px)');
  const layout = getQueueLayout(queueCount, isLargeScreen);
  const enableGridScroll = layout.mode === 'wrap' || layout.mode === 'multi';
  const effectiveColumns = queueCount > 0 && queueCount < COLUMNS ? queueCount : COLUMNS;
  const stackQueueMetrics = queueCount >= 6;
  const canEditSla = user?.edit_blip_sla === 'Y';

  const tempoTitleColor = isLight ? 'rgba(0, 0, 0, 0.55)' : 'rgba(255, 255, 255, 0.6)';
  const editSlaBorder = isLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.25)';
  const editSlaColor = isLight ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)';
  const editSlaPillBg = isLight ? 'transparent' : 'transparent';
  const updatePillBorder = isLight ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.2)';
  const updatePillBg = isLight ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.35)';
  const updatePillColor = isLight ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255, 255, 255, 0.7)';

  const formatSecondsToMinutes = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const minutes = Number(value) / 60;
    if (!Number.isFinite(minutes)) return '';
    const rounded = Number(minutes.toFixed(2));
    return `${rounded}`.replace(/\.00$/, '');
  };

  const handleOpenSlaDialog = () => {
    setSlaForm({
      first_response_time: formatSecondsToMinutes(slaData?.first_response_time),
      attendance_time: formatSecondsToMinutes(slaData?.attendance_time),
      wait_time: formatSecondsToMinutes(slaData?.wait_time),
      response_time: formatSecondsToMinutes(slaData?.response_time),
    });
    setSlaDialogOpen(true);
  };

  const handleCloseSlaDialog = () => {
    setSlaDialogOpen(false);
  };

  const handleChangeSla = (field) => (event) => {
    setSlaForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmitSla = async () => {
    const payload = {
      first_response_time: Number(slaForm.first_response_time),
      attendance_time: Number(slaForm.attendance_time),
      wait_time: Number(slaForm.wait_time),
      response_time: Number(slaForm.response_time),
    };

    if (
      !Number.isFinite(payload.first_response_time) ||
      !Number.isFinite(payload.attendance_time) ||
      !Number.isFinite(payload.wait_time) ||
      !Number.isFinite(payload.response_time)
    ) {
      ShowMessage('error', 'Ops..', 'Preencha todos os campos com valores numéricos (em minutos).');
      return;
    }

    const response = await postSla(urlSla, payload);
    if (response?.success) {
      ShowMessage('success', 'Sucesso', response.msg || 'SLA atualizado com sucesso.');
      setSlaDialogOpen(false);
      setChanged((current) => !current);
    } else {
      ShowMessage('error', 'Ops..', response.msg || 'Falha ao atualizar SLA.');
    }
  };

  const timeCards = useMemo(() => {
    const getStatus = (avgSeconds, slaSeconds) => {
      if (!Number.isFinite(avgSeconds) || !Number.isFinite(slaSeconds) || slaSeconds <= 0) {
        return null;
      }
      const ratio = avgSeconds / slaSeconds;
      if (ratio <= 0.75) return 'ok';
      if (ratio < 1) return 'warn';
      return 'alert';
    };

    const getPercent = (avgSeconds, slaSeconds) => {
      if (!Number.isFinite(avgSeconds) || !Number.isFinite(slaSeconds) || slaSeconds <= 0) {
        return null;
      }
      return Math.round((avgSeconds / slaSeconds) * 100);
    };

    // [MUDANÇA] Regra de alerta crítico para destacar cenários extremos.
    const getIsCritical = (percent) => Number.isFinite(percent) && percent >= 150;

    const avgWaitSeconds = parseDurationToSeconds(timingsData?.avgWaitTime);
    const avgResponseSeconds = parseDurationToSeconds(timingsData?.avgResponseTime);
    const avgAttendanceSeconds = parseDurationToSeconds(timingsData?.avgAttendanceTime);
    const avgFirstResponseSeconds = parseDurationToSeconds(timingsData?.avgFirstResponseTime);

    const waitPercent = getPercent(avgWaitSeconds, Number(slaData?.wait_time));
    const firstResponsePercent = getPercent(avgFirstResponseSeconds, Number(slaData?.first_response_time));
    const responsePercent = getPercent(avgResponseSeconds, Number(slaData?.response_time));
    const attendancePercent = getPercent(avgAttendanceSeconds, Number(slaData?.attendance_time));

    return [
      {
        key: 'avgWaitTime',
        title: 'Tempo médio de espera',
        value: formatDuration(timingsData?.avgWaitTime),
        status: getStatus(avgWaitSeconds, Number(slaData?.wait_time)),
        percent: waitPercent,
        isCritical: getIsCritical(waitPercent),
        tooltip:
          'Descreve o tempo desde que o ticket foi criado até ele ser aberto, ou seja, atribuído ao atendente. Basicamente o tempo que ele passa no bot antes de ser atribuído a um humano.',
      },
      {
        key: 'avgFirstResponseTime',
        title: 'Tempo médio de 1ª resposta',
        value: formatDuration(timingsData?.avgFirstResponseTime),
        status: getStatus(avgFirstResponseSeconds, Number(slaData?.first_response_time)),
        percent: firstResponsePercent,
        isCritical: getIsCritical(firstResponsePercent),
        tooltip:
          'Descreve o tempo desde a criação do ticket até o atendente mandar a primeira mensagem.',
      },
      {
        key: 'avgResponseTime',
        title: 'Tempo médio de resposta',
        value: formatDuration(timingsData?.avgResponseTime),
        status: getStatus(avgResponseSeconds, Number(slaData?.response_time)),
        percent: responsePercent,
        isCritical: getIsCritical(responsePercent),
        tooltip:
          'Descreve o tempo médio de resposta do atendente, quanto tempo leva entre uma mensagem do cliente e a sua reposta.',
      },
      {
        key: 'avgAttendanceTime',
        title: 'Tempo médio de atendimento',
        value: formatDuration(timingsData?.avgAttendanceTime),
        status: getStatus(avgAttendanceSeconds, Number(slaData?.attendance_time)),
        percent: attendancePercent,
        isCritical: getIsCritical(attendancePercent),
        tooltip:
          'Descreve o tempo médio total de atendimento, desde a atribuição do ticket ao atendente até o seu fechamento.',
      },
    ];
  }, [timingsData, slaData]);

  // [MUDANÇA] Indicador de risco em tempo real (topo da coluna direita)
  const riskSummary = useMemo(() => {
    const items = data?.items || [];
    const attendanceSlaSeconds = Number(slaData?.attendance_time);
    const firstResponseSlaSeconds = Number(slaData?.first_response_time);

    let atRiskCount = 0;
    let nearLimitCount = 0;

    items.forEach((ticket) => {
      const attendanceSeconds = parseDurationToSecondsForRisk(ticket?.attendanceTime);
      const firstResponseSeconds = parseDurationToSecondsForRisk(ticket?.firstResponseTime);

      const percents = [];
      if (
        Number.isFinite(attendanceSeconds) &&
        Number.isFinite(attendanceSlaSeconds) &&
        attendanceSlaSeconds > 0
      ) {
        percents.push((attendanceSeconds / attendanceSlaSeconds) * 100);
      }
      if (
        Number.isFinite(firstResponseSeconds) &&
        Number.isFinite(firstResponseSlaSeconds) &&
        firstResponseSlaSeconds > 0
      ) {
        percents.push((firstResponseSeconds / firstResponseSlaSeconds) * 100);
      }

      if (!percents.length) return;

      const maxPercent = Math.max(...percents);
      if (maxPercent >= 80) {
        atRiskCount += 1;
      } else if (maxPercent >= 60) {
        nearLimitCount += 1;
      }
    });

    return { atRiskCount, nearLimitCount };
  }, [data, slaData]);

  const slaCards = useMemo(
    () => [
      {
        key: 'wait_time',
        title: 'Espera',
        value: formatSecondsToDuration(slaData?.wait_time),
      },
      {
        key: 'first_response_time',
        title: '1ª resposta',
        value: formatSecondsToDuration(slaData?.first_response_time),
      },
      {
        key: 'response_time',
        title: 'Resposta',
        value: formatSecondsToDuration(slaData?.response_time),
      },
      {
        key: 'attendance_time',
        title: 'Atendimento',
        value: formatSecondsToDuration(slaData?.attendance_time),
      },
    ],
    [slaData]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsToRefresh((prev) => {
        if (prev <= 1) {
          setChanged((current) => !current);
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      className={`blipMonitoringPage${isLight ? ' blipMonitoringPage--light' : ''}`}
      sx={{
        p: 2,
        height: { xs: 'auto', md: 'calc(100vh - 88px)' },
        overflow: { xs: 'visible', md: 'hidden' },
        boxSizing: 'border-box',
        position: 'relative',
        display: { xs: 'block', md: 'flex' },
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'stretch' },
      }}
    >
      <Box
        sx={{
          flex: { md: '0 0 80%' },
          maxWidth: { md: '80%' },
          height: { xs: 'auto', md: '100%' },
          overflowY: enableGridScroll ? 'auto' : 'hidden',
          overflowX: 'hidden',
          pr: { md: '2%' },
          alignSelf: { md: 'stretch' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: { xs: 1.2, md: 1.4 },
          }}
        >
          {[
            { key: 'all', label: 'Todos' },
            { key: 'sla_overrun', label: 'Apenas SLA estourado' },
            { key: 'above_30m', label: 'Apenas acima de 30 min' },
            { key: 'waiting_response', label: 'Apenas aguardando resposta' },
          ].map((filterItem) => (
            <Button
              key={filterItem.key}
              size="small"
              variant={ticketFilter === filterItem.key ? 'contained' : 'outlined'}
              onClick={() => setTicketFilter(filterItem.key)}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                fontWeight: 600,
                px: 1.4,
                py: 0.4,
                fontSize: '0.75rem',
              }}
            >
              {filterItem.label}
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            display: 'grid',
            height: '100%',
            gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
            gridTemplateRows:
              layout.mode === 'single'
                ? '1fr'
                : layout.mode === 'split'
                  ? 'repeat(2, minmax(0, 1fr))'
                  : 'none',
            gridAutoRows:
              layout.mode === 'wrap'
                ? '100%'
                : layout.mode === 'multi'
                  ? '50%'
                  : '1fr',
            gridAutoFlow: 'row',
            alignContent: 'start',
            columnGap: { xs: 2, md: '1.2vw' },
            rowGap: { xs: 2, md: '1.2vw' },
          }}
        >
          {filteredQueues.map((queue, index) => (
            <Box
              key={queue.id}
              sx={{
                minHeight: 0,
                ...(layout.mode === 'split' ? getGridPlacement(index, queueCount) : {}),
              }}
            >
              <AttendantQueue
                attendantName={queue.name}
                tickets={queue.tickets}
                attendantMetrics={attendantsById.get(String(queue.id))}
                slaMetrics={slaData}
                stackMetrics={stackQueueMetrics}
              />
            </Box>
          ))}
          {queueCount === 0 && (
            <Box sx={{ gridColumn: `1 / span ${COLUMNS}` }}>
              <Typography color="text.secondary">Nenhum ticket em andamento.</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: { md: '0 0 20%' },
          maxWidth: { md: '20%' },
          height: { xs: 'auto', md: '100%' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: { md: '1.2vw' },
        }}
      >
        <TotalsCard opened={totalsFromTimings.opened} closed={totalsFromTimings.closed} />
        <Box
          sx={{
            borderRadius: '0.7em',
            border: isLight ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.2)',
            background: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.04)',
            px: 1.3,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.35,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.92rem',
              fontWeight: 700,
              color: isLight ? '#B71C1C' : '#FF8A80',
              lineHeight: 1.25,
            }}
          >
            {riskSummary.atRiskCount} atendimentos em risco
          </Typography>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: isLight ? '#8A5B00' : '#FFD54F',
              lineHeight: 1.25,
            }}
          >
            {riskSummary.nearLimitCount} pr&oacute;ximos do limite
          </Typography>
        </Box>
        <Box
          sx={{
            flex: '0 0 70%',
            display: 'grid',
            gridTemplateRows: 'repeat(4, minmax(0, 1fr))',
            rowGap: { md: '1.2vw' },
            columnGap: 0,
            '@media (max-width: 1599px)': {
              flex: '0 0 50%',
              gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              columnGap: '0.8vw',
              rowGap: '0.8vw',
            },
          }}
        >
          {timeCards.map((card) => (
            <TimeCard
              key={card.key}
              title={card.title}
              value={card.value}
              status={card.status}
              percent={card.percent}
              isCritical={card.isCritical}
              tooltip={card.tooltip}
            />
          ))}
        </Box>
        <Box
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '24px',
              '@media (max-width: 1599px)': {
                justifyContent: 'flex-start',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                color: tempoTitleColor,
              }}
            >
              TEMPO MÁXIMO PARA
            </Typography>
            {canEditSla && (
              <Box
                component="button"
                type="button"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  borderRadius: 6,
                  border: editSlaBorder,
                  background: editSlaPillBg,
                  color: editSlaColor,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  padding: '4px 8px',
                  cursor: 'pointer',
                }}
                onClick={handleOpenSlaDialog}
              >
                EDITAR SLA
              </Box>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                gap: { md: '0.8vw' },
              }}
            >
              {slaCards.map((card) => (
                <SlaCard key={card.key} title={card.title} value={card.value} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog open={slaDialogOpen} onClose={handleCloseSlaDialog} fullWidth maxWidth="sm">
        <DialogTitle>Editar SLA</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
            <TextField
              label="1ª resposta (min)"
              type="number"
              value={slaForm.first_response_time}
              onChange={handleChangeSla('first_response_time')}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
              variant="filled"
            />
            <TextField
              label="Atendimento (min)"
              type="number"
              value={slaForm.attendance_time}
              onChange={handleChangeSla('attendance_time')}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
              variant="filled"
            />
            <TextField
              label="Espera (min)"
              type="number"
              value={slaForm.wait_time}
              onChange={handleChangeSla('wait_time')}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
              variant="filled"
            />
            <TextField
              label="Resposta (min)"
              type="number"
              value={slaForm.response_time}
              onChange={handleChangeSla('response_time')}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
              variant="filled"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button onClick={handleCloseSlaDialog} variant="outlined" color="error">
            Cancelar
          </Button>
          <Button onClick={handleSubmitSla} variant="outlined" color="success">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          position: { md: 'absolute' },
          bottom: { md: 10 },
          right: { md: '6%' },
          '@media (min-width: 1600px)': {
            right: '8%',
          },
          padding: '4px 10px',
          borderRadius: '999px',
          border: updatePillBorder,
          background: updatePillBg,
          color: updatePillColor,
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          display: { xs: 'none', md: 'inline-flex' },
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <AccessTimeIcon
          sx={{
            fontSize: '0.85rem',
            opacity: isLight ? 0.55 : 0.7,
          }}
        />
        Atualiza em {secondsToRefresh}s
      </Box>
    </Box>
  );
}
